// ============================================================
// HomeHeader.jsx
//
// Hero section of the home page featuring:
//   - Fullscreen background video with dark overlay
//   - Announcement ticker at the top
//   - Brand headline and tagline
//   - Interactive 4-step service flow cards
//   - Decorative animated side text
//
// Steps are clickable — active step is highlighted in gold.
// ============================================================

import React, { useState } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";

// ── MUI Icons ────────────────────────────────────────────────
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import Looks3Icon from "@mui/icons-material/Looks3";
import Looks4Icon from "@mui/icons-material/Looks4";
import CodeIcon from "@mui/icons-material/Code";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import SchoolIcon from "@mui/icons-material/School";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// ── Internal Components & Assets ─────────────────────────────
import Announcement from "./Announcement";
import videoSource from "../assets/videos/prodevconsultpromo.mp4";

// ============================================================
//  CONSTANTS
// ============================================================

/**
 * Brand colors — consistent with the rest of the platform.
 */
const BRAND = {
    dark:  "#100720",
    gold:  "#E5C95F",
    white: "#FFFFFF",
};

/**
 * STEPS
 * The four service pillars shown as interactive cards.
 * Each step has a number icon, step icon, title, and subtitle.
 *
 * To add or reorder steps, update this array only.
 */
const STEPS = [
    {
        id:       1,
        numIcon:  <LooksOneIcon />,
        icon:     <CodeIcon sx={{ fontSize: 28 }} />,
        title:    "Build",
        subtitle: "Fullstack App",
    },
    {
        id:       2,
        numIcon:  <LooksTwoIcon />,
        icon:     <BusinessCenterIcon sx={{ fontSize: 28 }} />,
        title:    "Consult",
        subtitle: "Consultancy",
    },
    {
        id:       3,
        numIcon:  <Looks3Icon />,
        icon:     <SchoolIcon sx={{ fontSize: 28 }} />,
        title:    "Train",
        subtitle: "Mentorship",
    },
    {
        id:       4,
        numIcon:  <Looks4Icon />,
        icon:     <AutoAwesomeIcon sx={{ fontSize: 28 }} />,
        title:    "Innovate",
        subtitle: "Generative AI",
    },
];

// ============================================================
//  ANIMATIONS
// ============================================================

/**
 * floatInAndOut
 * Used on the decorative side text — fades and slides
 * in and out on a 6-second loop.
 */
const floatInAndOut = keyframes`
    0%   { opacity: 0;   transform: translateX(-20px) rotate(-90deg); }
    50%  { opacity: 0.6; transform: translateX(0)     rotate(-90deg); }
    100% { opacity: 0;   transform: translateX(-20px) rotate(-90deg); }
`;

/**
 * pulseGlow
 * Subtle pulsing glow on the active step card.
 */
const pulseGlow = keyframes`
    0%   { box-shadow: 0 10px 25px rgba(229,201,95,0.3); }
    50%  { box-shadow: 0 10px 40px rgba(229,201,95,0.55); }
    100% { box-shadow: 0 10px 25px rgba(229,201,95,0.3); }
`;

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * HeaderContainer
 * Full-height hero wrapper with video background.
 * paddingTop matches the fixed Header height so content
 * doesn't hide behind the navbar.
 */
const HeaderContainer = styled("div")({
    textAlign: "center",
    padding: "80px 20px 60px 20px",
    color: BRAND.white,
    minHeight: "700px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    fontFamily: "'Poppins', sans-serif",
    position: "relative",
    overflow: "hidden",
});

/**
 * BackgroundVideo
 * Fullscreen video that sits behind all content.
 * zIndex: -1 keeps it below the overlay and content.
 */
const BackgroundVideo = styled("video")({
    position: "absolute",
    top: 0, left: 0,
    width: "100%", height: "100%",
    objectFit: "cover",
    zIndex: -1,
});

/**
 * VideoOverlay
 * Dark brand-colored tint over the video for
 * text readability and brand consistency.
 */
const VideoOverlay = styled("div")({
    position: "absolute",
    top: 0, left: 0,
    width: "100%", height: "100%",
    background: "rgba(16,7,32,0.72)",
    zIndex: 0,
});

/**
 * ServicesWrapper
 * Centers the headline, tagline, and step cards
 * above the video overlay (zIndex: 1).
 */
const ServicesWrapper = styled("div")({
    zIndex: 1,
    width: "100%",
    maxWidth: "1100px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "20px",
    textAlign: "center",
});

/**
 * FlowContainer
 * Horizontal row of step cards with a progress line
 * connecting them on desktop. Stacks vertically on mobile.
 */
const FlowContainer = styled("div")({
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "20px",
    width: "100%",
    maxWidth: "1000px",
    padding: "20px 0",
    "@media (max-width: 768px)": {
        flexDirection: "column",
        alignItems: "center",
    },
});

/**
 * ProgressLine
 * Subtle gold horizontal line connecting the step cards.
 * Hidden on mobile where cards stack vertically.
 */
const ProgressLine = styled("div")({
    position: "absolute",
    top: "35%",
    left: "5%",
    right: "5%",
    height: "2px",
    background: `linear-gradient(to right, transparent, rgba(229,201,95,0.4), transparent)`,
    zIndex: 0,
    "@media (max-width: 768px)": {
        display: "none",
    },
});

/**
 * ServiceStep
 * Clickable card for each service pillar.
 * Active card is gold with a pulsing glow animation.
 * Inactive cards are dark glass with subtle border.
 */
const ServiceStep = styled("div")(({ active }) => ({
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "28px 18px",
    backgroundColor: active ? BRAND.gold : "rgba(255,255,255,0.05)",
    color: active ? BRAND.dark : BRAND.white,
    borderRadius: "18px",
    width: "220px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
    backdropFilter: "blur(10px)",
    border: active
        ? `2px solid ${BRAND.gold}`
        : "1px solid rgba(255,255,255,0.1)",
    animation: active ? `${pulseGlow} 2.5s infinite ease-in-out` : "none",

    "&:hover": {
        transform: "translateY(-8px)",
        backgroundColor: active ? BRAND.gold : "rgba(255,255,255,0.1)",
    },

    "& svg": {
        fontSize: "2rem",
        marginBottom: "4px",
    },
}));

/**
 * DecorativeSideText
 * Rotating animated text on the side of the hero.
 * Pure decoration — pointerEvents disabled.
 */
const DecorativeSideText = styled("div")({
    position: "absolute",
    left: "600px",
    bottom: "100px",
    transformOrigin: "left bottom",
    color: BRAND.gold,
    fontWeight: 700,
    fontSize: "0.78rem",
    letterSpacing: "3px",
    textTransform: "uppercase",
    opacity: 0.5,
    animation: `${floatInAndOut} 20s infinite ease-in-out`,
    pointerEvents: "none",
    zIndex: 2,
    "@media (max-width: 1024px)": {
        display: "none", // Hide on smaller screens to avoid overlap
    },
});

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function HomeHeader() {

    // activeStep tracks which service card is highlighted
    const [activeStep, setActiveStep] = useState(1);

    // ============================================================
    //  RENDER
    // ============================================================

    return (
        <HeaderContainer>

            {/* ── Background Video ── */}
            <BackgroundVideo autoPlay loop muted playsInline>
                <source src={videoSource} type="video/mp4" />
            </BackgroundVideo>

            {/* ── Dark Overlay ── */}
            <VideoOverlay />

            {/* ── Announcement Ticker ── */}
            {/* Sits at the very top above all other content */}
            <Box sx={{ zIndex: 2, width: "100%" }}>
                <Announcement />
            </Box>

            {/* ── Hero Content ── */}
            <ServicesWrapper>

                {/* Platform label chip */}
                <Chip
                    label="Finnish Online Training & Consultation Platform"
                    sx={{
                        backgroundColor: "rgba(229,201,95,0.15)",
                        color: BRAND.gold,
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        letterSpacing: "1px",
                        border: `1px solid rgba(229,201,95,0.3)`,
                        mb: 2,
                    }}
                />

                {/* Main headline */}
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: "1.8rem", sm: "2.4rem", md: "2.8rem" },
                        color: BRAND.gold,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        fontFamily: "'Georgia', serif",
                        lineHeight: 1.2,
                        mb: 2,
                    }}
                >
                    SaaS,Embedded Firmwares & IoT
                </Typography>

                {/* Tagline */}
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: { xs: "0.95rem", md: "1.1rem" },
                        color: "rgba(255,255,255,0.85)",
                        maxWidth: "680px",
                        mb: 5,
                        lineHeight: 1.8,
                    }}
                >
                    A step‑by‑step path blending scalable SaaS solutions with
                    hands‑on mentorship. We guide you through every stage of
                    modern software engineering.
                </Typography>

                {/* ── Step Cards ── */}
                <FlowContainer>

                    {/* Connecting progress line */}
                    <ProgressLine />

                    {/* Render each step from STEPS array */}
                    {STEPS.map((step) => (
                        <ServiceStep
                            key={step.id}
                            active={activeStep === step.id}
                            onClick={() => setActiveStep(step.id)}
                        >
                            {/* Number icon — small, top of card */}
                            <Box sx={{
                                opacity: 0.5,
                                "& svg": { fontSize: "1rem !important" },
                            }}>
                                {step.numIcon}
                            </Box>

                            {/* Main icon */}
                            {step.icon}

                            {/* Step title */}
                            <Typography
                                variant="subtitle1"
                                fontWeight={800}
                                sx={{
                                    color: activeStep === step.id
                                        ? BRAND.dark
                                        : BRAND.white,
                                    fontFamily: "'Georgia', serif",
                                }}
                            >
                                {step.title}
                            </Typography>

                            {/* Step subtitle */}
                            <Typography
                                variant="caption"
                                sx={{
                                    color: activeStep === step.id
                                        ? "rgba(16,7,32,0.65)"
                                        : "rgba(255,255,255,0.55)",
                                    fontSize: "0.78rem",
                                }}
                            >
                                {step.subtitle}
                            </Typography>

                            {/* Arrow shown only on active step */}
                            {activeStep === step.id && (
                                <ArrowForwardIcon
                                    sx={{
                                        fontSize: "1rem !important",
                                        color: BRAND.dark,
                                        opacity: 0.6,
                                    }}
                                />
                            )}
                        </ServiceStep>
                    ))}

                </FlowContainer>

            </ServicesWrapper>

        </HeaderContainer>
    );
}
